import type { MessageStatus, RunStatus } from '../types';

export function normalizeQueueStatus(status?: string | null): MessageStatus | null {
  if (!status) {
    return null;
  }
  const normalized = status.toLowerCase();
  if (
    normalized === 'queued' ||
    normalized === 'processing' ||
    normalized === 'done' ||
    normalized === 'error' ||
    normalized === 'canceled'
  ) {
    return normalized as MessageStatus;
  }
  return null;
}

export function messageStatusFromRun(status?: RunStatus | null): MessageStatus | null {
  if (!status) {
    return null;
  }
  if (status === 'running') {
    return 'processing';
  }
  return status;
}

type ResolveTurnStatusInput = {
  messageStatus: MessageStatus;
  queueStatus?: string | null;
  runStatus?: RunStatus | null;
  assistantStatus?: MessageStatus | null;
};

export function resolveTurnStatus(input: ResolveTurnStatusInput): MessageStatus {
  const queueStatus = normalizeQueueStatus(input.queueStatus);
  const runStatus = messageStatusFromRun(input.runStatus);

  if (queueStatus === 'queued') {
    return 'queued';
  }
  if (runStatus === 'processing') {
    return 'processing';
  }
  // A job can be briefly marked as "processing" while being deferred before a run exists.
  // Keep it visually queued until we have an actual run for this message.
  if (queueStatus === 'processing' && !runStatus) {
    return input.messageStatus === 'queued' ? 'queued' : 'processing';
  }
  if (runStatus) {
    return runStatus;
  }
  if (queueStatus) {
    return queueStatus;
  }
  if (input.assistantStatus === 'done') {
    return 'done';
  }
  if (input.assistantStatus === 'error') {
    return 'error';
  }
  return input.messageStatus;
}

type ResolveComposerQueueStateInput = {
  holdQueuedMessages: boolean;
  effectiveConversationStatus?: 'idle' | 'queued' | 'processing' | null;
  hasActiveRun: boolean;
  hasPendingQueue: boolean;
};

export function shouldQueueComposerSubmissions(
  input: ResolveComposerQueueStateInput
): boolean {
  return (
    input.holdQueuedMessages ||
    input.hasActiveRun ||
    input.hasPendingQueue ||
    input.effectiveConversationStatus === 'queued'
  );
}

type ResolveQueuedTurnControlsInput = {
  messageId: string;
  turnStatus: MessageStatus;
  queueItem?:
    | {
        id?: string | null;
        status?: string | null;
        manual_hold?: boolean | null;
      }
    | null;
};

export type QueuedTurnControls = {
  itemId: string;
  manualHold: boolean;
  showHoldToggle: boolean;
  showStartNow: boolean;
  showCancel: boolean;
  cancelRequiresConfirmation: boolean;
};

export function resolveQueuedTurnControls(
  input: ResolveQueuedTurnControlsInput
): QueuedTurnControls | null {
  const queueStatus = normalizeQueueStatus(input.queueItem?.status);
  const isQueued = input.turnStatus === 'queued' || queueStatus === 'queued';
  const isProcessing = input.turnStatus === 'processing';
  if (!isQueued && !isProcessing) {
    return null;
  }
  const itemId = input.queueItem?.id || input.messageId;
  const manualHold = isQueued && Boolean(input.queueItem?.manual_hold);
  return {
    itemId,
    manualHold,
    showHoldToggle: isQueued,
    showStartNow: isQueued && manualHold,
    showCancel: true,
    cancelRequiresConfirmation: isProcessing
  };
}
